import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const API_URL = import.meta.env.VITE_API_URL || 'https://qihmh0pa24.execute-api.us-west-2.amazonaws.com'

interface SettingsTabProps {
  onLogout: () => void
}

export function SettingsTab({ onLogout }: SettingsTabProps) {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [formData, setFormData] = useState({
    agentName: '',
    agentPhone: '',
    calendlyUrl: '',
    cities: '',
    priceRanges: '',
    brandColor: '#3b82f6',
  })

  useEffect(() => {
    fetchSettings()
  }, [])

  const fetchSettings = async () => {
    const jwt = localStorage.getItem('hellabots_jwt')

    if (!jwt) {
      navigate('/settings')
      return
    }

    try {
      const response = await fetch(`${API_URL}/settings`, {
        headers: {
          'Authorization': `Bearer ${jwt}`,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('hellabots_jwt')
          localStorage.removeItem('hellabots_clientId')
          navigate('/settings')
          return
        }
        throw new Error(data.error || 'Failed to fetch settings')
      }

      setFormData({
        agentName: data.settings.agentName || '',
        agentPhone: data.settings.agentPhone || '',
        calendlyUrl: data.settings.calendlyUrl || '',
        cities: data.settings.cities?.join(', ') || '',
        priceRanges: data.settings.priceRanges?.join(', ') || '',
        brandColor: data.settings.brandColor || '#3b82f6',
      })
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)
    setSaving(true)

    const jwt = localStorage.getItem('hellabots_jwt')

    if (!jwt) {
      navigate('/settings')
      return
    }

    try {
      const response = await fetch(`${API_URL}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          agentName: formData.agentName,
          agentPhone: formData.agentPhone,
          calendlyUrl: formData.calendlyUrl,
          cities: formData.cities.split(',').map((c) => c.trim()).filter(Boolean),
          priceRanges: formData.priceRanges.split(',').map((p) => p.trim()).filter(Boolean),
          brandColor: formData.brandColor,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('hellabots_jwt')
          localStorage.removeItem('hellabots_clientId')
          navigate('/settings')
          return
        }
        throw new Error(data.error || 'Failed to update settings')
      }

      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Agent Information */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Agent Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Agent Name
            </label>
            <input
              type="text"
              value={formData.agentName}
              onChange={(e) => setFormData({ ...formData, agentName: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={formData.agentPhone}
              onChange={(e) => setFormData({ ...formData, agentPhone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Calendly */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Scheduling</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Calendly URL
          </label>
          <input
            type="url"
            value={formData.calendlyUrl}
            onChange={(e) => setFormData({ ...formData, calendlyUrl: e.target.value })}
            placeholder="https://calendly.com/your-link"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Market Settings */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Market Settings</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cities (comma-separated)
            </label>
            <input
              type="text"
              value={formData.cities}
              onChange={(e) => setFormData({ ...formData, cities: e.target.value })}
              placeholder="Castro Valley, Fremont, Hayward, San Leandro"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              These cities will be shown to leads during conversations
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Price Ranges (comma-separated)
            </label>
            <input
              type="text"
              value={formData.priceRanges}
              onChange={(e) => setFormData({ ...formData, priceRanges: e.target.value })}
              placeholder="Under $800K, $800K - $1.2M, $1.2M - $2M, $2M+"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              These price ranges will be shown to buyer leads
            </p>
          </div>
        </div>
      </div>

      {/* Branding */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Branding</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brand Color
          </label>
          <div className="flex items-center gap-4">
            <input
              type="color"
              value={formData.brandColor}
              onChange={(e) => setFormData({ ...formData, brandColor: e.target.value })}
              className="h-12 w-24 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={formData.brandColor}
              onChange={(e) => setFormData({ ...formData, brandColor: e.target.value })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          Settings updated successfully!
        </div>
      )}

      {/* Submit Button */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <button
          type="button"
          onClick={onLogout}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}
